import { phoneSuffix } from './inboundSms';

/**
 * The rules of one SMS conversation, kept out of the component so they are
 * tested without a DOM (Kevin, 2026-10-07).
 */

/** A message as GET /telephony/inbound-sms returns it, both directions. */
export interface StoredSms {
  id?: string;
  direction?: string;
  from?: string;
  to?: string;
  body?: string;
  received_at?: string;
  status?: string;
  prospect_phone?: string;
}

export interface ThreadMessage {
  key: string;
  direction: 'inbound' | 'outbound';
  body: string;
  at: number;
  status: string | null;
  /** Our number in this exchange: the one written to, or written from. */
  ours: string;
}

/**
 * Only this contact's messages, oldest first so the newest sits at the
 * bottom. The contact is matched on the last nine digits, the store and the
 * operator not writing numbers the same way.
 */
export function threadMessages(stored: StoredSms[], phone: string): ThreadMessage[] {
  const suffix = phoneSuffix(phone);
  return (stored || [])
    .filter((m) => {
      const other = m.direction === 'inbound' ? m.from : m.to;
      return !!suffix && (phoneSuffix(other || '') === suffix || phoneSuffix(m.prospect_phone || '') === suffix);
    })
    .map((m) => {
      const inbound = m.direction === 'inbound';
      return {
        key: m.id || `${m.direction}-${m.received_at}`,
        direction: inbound ? ('inbound' as const) : ('outbound' as const),
        body: m.body || '',
        at: m.received_at ? new Date(m.received_at).getTime() : 0,
        status: inbound ? null : (m.status || null),
        ours: (inbound ? m.to : m.from) || '',
      };
    })
    .sort((a, b) => a.at - b.at);
}

/**
 * The reply leaves from the number this conversation already uses, when it
 * can send SMS (Pierrejean, 2026-09-30: a reply from another number arrives
 * from a stranger). Undefined lets the caller fall back to its own choice.
 */
export function replySender(
  messages: ThreadMessage[],
  capable: Array<{ phoneNumber?: string | null }>,
): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const ours = messages[i]?.ours;
    if (ours && capable.some((n) => n.phoneNumber === ours)) return ours;
  }
  return undefined;
}

/**
 * What the caller is told about a sent SMS: only the two outcomes that
 * matter (Kevin, 2026-10-07). Twilio's intermediate words ("queued",
 * "sending", "sent") said nothing useful, and in English.
 */
export type SmsDelivery = 'delivered' | 'failed';

export function smsDelivery(status: string | null | undefined): SmsDelivery | null {
  const s = (status || '').toLowerCase();
  if (s === 'delivered' || s === 'read') return 'delivered';
  if (s === 'failed' || s === 'undelivered') return 'failed';
  return null;
}
